import { Badge, Button, Card, CardBody, CardHeader, Select, Spinner, Textarea } from '@chopchop-se/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReceptionClaimLine, ReceptionClaimReason } from '@warehouse/shared';
import { AlertTriangle, ArrowLeft, Check, Plus, Trash2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { PageHeader } from '../../../components/PageHeader';
import { formatDate, formatDateTime, formatQty } from '../../../lib/format';
import { errorMessage, toastError, toastSuccess } from '../../../lib/notify';
import { useLiveQuery } from '../../../realtime/useLiveQuery';
import { pickingKeys, receptionApi } from '../api';
import { ShipmentStatusBadge } from '../components/StatusBadge';
import { commandId } from '../../../lib/command-id';

const REASONS: ReceptionClaimReason[] = [
  'missing',
  'damaged',
  'spoiled',
  'cold_chain',
  'received_more',
  'wrong_item',
  'quality',
  'other',
];

type DraftLine = ReceptionClaimLine & { key: string };

/**
 * `/portal/receive/<qrToken>` — the screen a restaurant lands on after scanning
 * the QR on a pallet label or packing slip (spec §5.18).
 *
 * Two outcomes, and the UI makes the common one one tap: "Allt är OK" confirms
 * the whole delivery. The other path registers a claim per line and, when the
 * damaged flag is set, notifies the warehouse immediately.
 *
 * The token is NOT the authorisation — the route requires a portal session and
 * the server re-checks restaurant membership. A forwarded photo of a label
 * reveals nothing.
 */
export function ReceiveDetailPage(): ReactNode {
  const { t } = useTranslation('pickingShipping');
  const { t: tc } = useTranslation('common');
  const { token = '' } = useParams();
  const queryClient = useQueryClient();

  const [note, setNote] = useState('');
  const [damaged, setDamaged] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [lines, setLines] = useState<DraftLine[]>([]);

  const delivery = useQuery({
    queryKey: pickingKeys.receptionView(token),
    queryFn: () => receptionApi.view(token),
    enabled: token.length > 0,
    retry: false,
  });
  useLiveQuery(['receptions', 'shipments'], pickingKeys.receptionView(token));

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['reception'] });
  };

  const confirm = useMutation({
    mutationFn: (allOk: boolean) =>
      receptionApi.confirm(token, { allOk, damaged, note: note.trim() || null }),
    onSuccess: async () => {
      toastSuccess(t('reception.confirmedToast'));
      setNote('');
      setDamaged(false);
      await invalidate();
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  const claim = useMutation({
    mutationFn: () =>
      receptionApi.claim(token, {
        damaged,
        note: note.trim() || null,
        lines: lines.map(({ key: _key, ...line }) => line),
      }),
    onSuccess: async (result) => {
      toastSuccess(t('reception.claimRegistered', { count: result.claims.length }), {
        description: t('reception.claimNumbers', {
          numbers: result.claims.map((c) => c.claimNo).join(', '),
        }),
      });
      setLines([]);
      setClaiming(false);
      setNote('');
      setDamaged(false);
      await invalidate();
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  if (delivery.isLoading) return <Spinner />;

  if (delivery.error) {
    return (
      <>
        <PageHeader title={t('reception.title')} />
        <Card>
          <CardBody>
            <p className="text-sm text-danger-700">{errorMessage(delivery.error)}</p>
            <Link className="mt-3 inline-block text-sm text-brand-600 underline" to="/portal/receive">
              {t('reception.incoming')}
            </Link>
          </CardBody>
        </Card>
      </>
    );
  }

  if (!delivery.data) return null;
  const data = delivery.data;

  return (
    <>
      <PageHeader
        breadcrumb={
          <Link to="/portal/receive" className="inline-flex items-center gap-1 hover:text-ink">
            <ArrowLeft className="size-3" aria-hidden /> {t('reception.incoming')}
          </Link>
        }
        title={data.shipmentNo}
        subtitle={`${data.orderNo} · ${t('reception.expected')} ${formatDate(data.deliveryDate)}`}
        actions={
          <div className="flex items-center gap-2">
            <ShipmentStatusBadge status={data.status} />
            {data.isConfirmed ? (
              <Badge tone="success" size="sm">
                {t('reception.eventKind.confirmed')}
              </Badge>
            ) : null}
          </div>
        }
      />

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <span className="text-sm font-semibold">{t('reception.lines')}</span>
          </CardHeader>
          <CardBody>
            {data.pallets.length > 0 ? (
              <p className="mb-3 flex flex-wrap gap-1.5 text-sm">
                {data.pallets.map((pallet) => (
                  <Badge key={pallet.lpn} tone="neutral" size="sm">
                    {pallet.lpn} ({pallet.packageCount})
                  </Badge>
                ))}
              </p>
            ) : null}

            <ul className="flex flex-col divide-y divide-surface-border" data-testid="reception-lines">
              {data.lines.map((line) => (
                <li key={line.orderLineId} className="flex items-center gap-3 py-2 text-sm">
                  <span className="font-mono text-xs text-ink-muted">{line.articleNo}</span>
                  <span className="flex-1">{line.articleName}</span>
                  <span className="font-mono font-semibold">
                    {formatQty(line.qtyBase, { unit: line.unitCode })}
                  </span>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <span className="text-sm font-semibold">{t('reception.confirmAll')}</span>
          </CardHeader>
          <CardBody className="flex flex-col gap-3">
            <p className="text-sm text-ink-muted">{t('reception.confirmAllHint')}</p>

            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-1"
                checked={damaged}
                onChange={(event) => {
                  setDamaged(event.target.checked);
                }}
              />
              <span>
                {t('reception.damaged')}
                <span className="block text-xs text-ink-muted">{t('reception.damagedHint')}</span>
              </span>
            </label>

            <Textarea
              rows={3}
              aria-label={t('reception.note')}
              placeholder={t('reception.notePlaceholder')}
              value={note}
              onChange={(event) => {
                setNote(event.target.value);
              }}
            />

            <Button
              icon={<Check className="size-4" />}
              loading={confirm.isPending}
              onClick={() => {
                confirm.mutate(true);
              }}
            >
              {t('reception.confirmAll')}
            </Button>

            <Button
              variant="secondary"
              icon={<AlertTriangle className="size-4" />}
              onClick={() => {
                setClaiming((value) => !value);
                if (lines.length === 0 && data.lines[0]) {
                  setLines([
                    {
                      key: commandId(),
                      orderLineId: data.lines[0].orderLineId,
                      articleId: data.lines[0].articleId,
                      qtyBase: '1',
                      reason: 'missing',
                      description: '',
                    },
                  ]);
                }
              }}
            >
              {t('reception.reportIssue')}
            </Button>
          </CardBody>
        </Card>
      </div>

      {claiming ? (
        <Card className="mt-4">
          <CardHeader>
            <span className="text-sm font-semibold">{t('reception.claimTitle')}</span>
          </CardHeader>
          <CardBody className="flex flex-col gap-3">
            {lines.map((line, index) => (
              <div key={line.key} className="grid gap-2 rounded-lg border border-surface-border p-3 sm:grid-cols-4">
                <Select
                  label={tc('common.article')}
                  value={line.articleId}
                  options={data.lines.map((l) => ({ value: l.articleId, label: l.articleName }))}
                  onChange={(event) => {
                    const picked = data.lines.find((l) => l.articleId === event.target.value);
                    setLines((prev) =>
                      prev.map((item, i) =>
                        i === index
                          ? {
                              ...item,
                              articleId: event.target.value,
                              orderLineId: picked?.orderLineId ?? item.orderLineId,
                            }
                          : item,
                      ),
                    );
                  }}
                />
                <label className="flex flex-col gap-1 text-sm">
                  <span className="font-medium">{t('reception.claimQty')}</span>
                  <input
                    className="rounded-lg border border-surface-border px-3 py-2"
                    inputMode="decimal"
                    value={line.qtyBase}
                    onChange={(event) => {
                      setLines((prev) =>
                        prev.map((item, i) => (i === index ? { ...item, qtyBase: event.target.value } : item)),
                      );
                    }}
                  />
                </label>
                <Select
                  label={t('reception.claimReason')}
                  value={line.reason}
                  options={REASONS.map((reason) => ({
                    value: reason,
                    label: t(`reception.reasons.${reason}`),
                  }))}
                  onChange={(event) => {
                    setLines((prev) =>
                      prev.map((item, i) =>
                        i === index ? { ...item, reason: event.target.value as ReceptionClaimReason } : item,
                      ),
                    );
                  }}
                />
                <div className="flex items-end gap-2">
                  <label className="flex flex-1 flex-col gap-1 text-sm">
                    <span className="font-medium">{t('reception.claimDescription')}</span>
                    <input
                      className="rounded-lg border border-surface-border px-3 py-2"
                      value={line.description}
                      onChange={(event) => {
                        setLines((prev) =>
                          prev.map((item, i) =>
                            i === index ? { ...item, description: event.target.value } : item,
                          ),
                        );
                      }}
                    />
                  </label>
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={t('reception.removeLine')}
                    onClick={() => {
                      setLines((prev) => prev.filter((_, i) => i !== index));
                    }}
                  >
                    <Trash2 className="size-4" aria-hidden />
                  </Button>
                </div>
              </div>
            ))}

            <div className="flex flex-wrap gap-2">
              <Button
                variant="ghost"
                size="sm"
                icon={<Plus className="size-4" />}
                disabled={data.lines.length === 0}
                onClick={() => {
                  const first = data.lines[0];
                  if (!first) return;
                  setLines((prev) => [
                    ...prev,
                    {
                      key: commandId(),
                      orderLineId: first.orderLineId,
                      articleId: first.articleId,
                      qtyBase: '1',
                      reason: 'missing',
                      description: '',
                    },
                  ]);
                }}
              >
                {t('reception.addLine')}
              </Button>
              <Button
                className="ml-auto"
                loading={claim.isPending}
                disabled={lines.length === 0}
                onClick={() => {
                  claim.mutate();
                }}
              >
                {t('reception.submitClaim')}
              </Button>
            </div>
          </CardBody>
        </Card>
      ) : null}

      {data.events.length > 0 ? (
        <Card className="mt-4">
          <CardHeader>
            <span className="text-sm font-semibold">{t('reception.history')}</span>
          </CardHeader>
          <CardBody>
            <ul className="flex flex-col gap-2 text-sm" data-testid="reception-events">
              {data.events.map((event) => (
                <li key={event.id} className="flex items-center gap-3">
                  <Badge tone={event.kind === 'damaged' ? 'danger' : 'neutral'} size="sm">
                    {t(`reception.eventKind.${event.kind}`)}
                  </Badge>
                  <span className="text-ink-muted">{formatDateTime(event.createdAt)}</span>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      ) : null}
    </>
  );
}
